import { InputAdornment } from '@mui/material';
import { array, number, object, string } from 'yup';

import { RHFAutocomplete, RHFDatePicker, RHFReactNumberFormat, RHFTextField } from '@zeta-core';
import { CAPintObject } from '../common/commons';

export const cashAssetsValidations = object().shape({
  institution: string(),
  accountType: string().nullable(),
  amount: string(),
  interestRate: number().nullable(),
  assetDescription: string(),
});

export const investmentAssetsValidations = object().shape({
  institution: string(),

  investmentType: string().nullable(),
  startDate: string(),
  endDate: string(),
  value: string(),
  assetDescription: string(),
});

export const businessAssetsValidations = object().shape({
  companyName: string(),
  location: string(),
  shareholderPercentage: number().max(100, 'Maximum 100').nullable().typeError('Please enter a valid number'),
  capOrInt: object()
    .shape({
      label: string(),
      value: string(),
    })
    .nullable(),
  incomeAmount: string(),
  interestRate: number().nullable(),
  value: string(),
  assetDescription: string(),
});

export const pensionAssetsValidations = object().shape({
  pensionName: string(),
  pensionType: string().nullable(),
  provider: string(),
  cetv: number().nullable(),
  transferValue: number().nullable(),
  startDate: string(),
  endDate: string(),
  assetLocation: string(),
  scheme: string(),
  policyNumber: string(),
  assetDescription: string(),
});

export const propertyAssetsValidations = object().shape({
  propertyName: string(),
  propertyType: string().nullable(),
  location: string(),
  income: string(),
  capOrInt: object()
    .shape({
      label: string().nullable(),
      value: string().nullable(),
    })
    .nullable(),
  interestRate: number().nullable(),
  startDate: string(),
  endDate: string(),
  currentValue: string(),
  assetDescription: string(),
});

export const otherAssetsValidations = object().shape({
  assetType: string(),
  location: string(),
  income: string(),
  capOrInt: object()
    .shape({
      label: string().nullable(),
      value: string().nullable(),
    })
    .nullable(),
  interestRate: number().nullable(),
  startDate: string(),
  endDate: string(),
  currentValue: string(),
  assetDescription: string(),
});

export const assetsValidations = object().shape({
  cashAssets: array().of(cashAssetsValidations),
  investmentAssets: array().of(investmentAssetsValidations),
  businessAssets: array().of(businessAssetsValidations),
  pensionAssets: array().of(pensionAssetsValidations),
  propertyAssets: array().of(propertyAssetsValidations),
  otherAssets: array().of(otherAssetsValidations),
});

export const assetsValidationSchema = object().shape({
  client: assetsValidations,
  partner: assetsValidations,
  joint: assetsValidations,
});

export const cashAssetsDefault = {
  institution: '',
  accountType: '',
  amount: 0,
  interestRate: 0,
  assetDescription: '',
  show: true,
};

export const investmentAssetsDefault = {
  institution: '',
  investmentType: '',
  startDate: '',
  endDate: '',
  value: 0,
  assetDescription: '',
  show: true,
};

export const businessAssetsDefault = {
  companyName: '',
  location: '',
  shareholderPercentage: 0,
  capOrInt: {
    label: '',
    value: '',
  },
  incomeAmount: 0,
  interestRate: 0,
  value: 0,
  assetDescription: '',
  show: true,
};

export const pensionAssetsDefault = {
  pensionName: '',
  pensionType: '',
  provider: '',
  cetv: 0,
  startDate: '',
  endDate: '',
  assetLocation: '',
  scheme: '',
  policyNumber: '',
  assetDescription: '',
  show: true,
};

export const propertyAssetsDefault = {
  propertyName: '',
  propertyType: '',
  location: '',
  income: 0,
  capOrInt: {
    label: '',
    value: '',
  },
  interestRate: 0,
  startDate: '',
  endDate: '',
  currentValue: 0,
  assetDescription: '',
  show: true,
};

export const otherAssetsDefault = {
  assetType: '',
  location: '',
  income: 0,
  capOrInt: {
    label: '',
    value: '',
  },
  interestRate: 0,
  startDate: '',
  endDate: '',
  currentValue: 0,
  assetDescription: '',
  show: true,
};

export const assetsDefault = {
  cashAssets: [cashAssetsDefault],
  investmentAssets: [investmentAssetsDefault],
  businessAssets: [businessAssetsDefault],
  pensionAssets: [pensionAssetsDefault],
  propertyAssets: [propertyAssetsDefault],
  otherAssets: [otherAssetsDefault],
};

export const assetsDefaultValues = {
  client: assetsDefault,
  partner: assetsDefault,
  joint: assetsDefault,
};

export const CASH_ASSETS_TABLE_STRUCTURE = (ffConfig, cashAssetAccountTypeOptions) => [
  {
    label: 'Institution',
    name: 'institution',
    component: RHFTextField,
    minWidth: 150,
    width: 150,
    show: ffConfig?.cashAssets?.fields?.institution?.active ?? true,
  },
  {
    label: 'Account Type',
    name: 'accountType',
    component: RHFAutocomplete,
    options: cashAssetAccountTypeOptions,
    minWidth: 150,
    width: 150,
    show: ffConfig?.cashAssets?.fields?.accountType?.active ?? true,
  },
  {
    label: 'Account Number',
    name: 'accountNumber',
    component: RHFTextField,
    minWidth: 150,
    width: 150,
    show: ffConfig?.cashAssets?.fields?.accountNumber?.active ?? true,
  },
  {
    label: 'Amount',
    name: 'amount',
    component: RHFReactNumberFormat,
    decimalScale: 2,
    thousandSeparator: true,
    fixedDecimalScale: true,
    prefix: true,
    textAlign: 'right',
    minWidth: 150,
    width: 150,
    show: ffConfig?.cashAssets?.fields?.amount?.active ?? true,
  },
  {
    label: 'Interest Rate',
    name: 'interestRate',
    component: RHFReactNumberFormat,
    textAlign: 'right',
    decimalScale: 2,
    InputProps: {
      endAdornment: <InputAdornment position="end">%</InputAdornment>,
    },
    minWidth: 150,
    width: 150,
    show: ffConfig?.cashAssets?.fields?.interestRate?.active ?? true,
  },
  {
    label: 'Description',
    name: 'assetDescription',
    component: RHFTextField,
    minWidth: 150,
    width: 150,
    show: ffConfig?.cashAssets?.fields?.assetDescription?.active ?? true,
  },
  {
    label: '',
    name: 'action',
    component: RHFReactNumberFormat,
    minWidth: 50,
    width: 50,
    show: true,
  },
];

export const INVESTMENT_ASSETS_TABLE_STRUCTURE = (ffConfig, investmentAssetAccountTypeOptions) => [
  {
    label: 'Institution',
    name: 'institution',
    component: RHFTextField,
    minWidth: 150,
    width: 150,
    show: ffConfig?.investmentAssets?.fields?.institution?.active ?? true,
  },
  {
    label: 'Investment Type',
    name: 'investmentType',
    component: RHFAutocomplete,
    options: investmentAssetAccountTypeOptions,
    minWidth: 150,
    width: 150,
    show: ffConfig?.investmentAssets?.fields?.investmentType?.active ?? true,
  },
  {
    label: 'Account Number',
    name: 'accountNumber',
    component: RHFTextField,
    minWidth: 150,
    width: 150,
    show: ffConfig?.investmentAssets?.fields?.accountNumber?.active ?? true,
  },
  {
    label: 'Start Date',
    name: 'startDate',
    component: RHFDatePicker,
    minWidth: 150,
    width: 150,
    show: ffConfig?.investmentAssets?.fields?.startDate?.active ?? true,
  },
  {
    label: 'End Date',
    name: 'endDate',
    component: RHFDatePicker,
    minWidth: 150,
    width: 150,
    show: ffConfig?.investmentAssets?.fields?.endDate?.active ?? true,
  },
  {
    label: 'Value',
    name: 'value',
    component: RHFReactNumberFormat,
    decimalScale: 2,
    thousandSeparator: true,
    fixedDecimalScale: true,
    prefix: true,
    textAlign: 'right',
    minWidth: 150,
    width: 150,
    show: ffConfig?.investmentAssets?.fields?.value?.active ?? true,
  },
  {
    label: 'Description',
    name: 'assetDescription',
    component: RHFTextField,
    minWidth: 150,
    width: 150,
    show: ffConfig?.investmentAssets?.fields?.assetDescription?.active ?? true,
  },
  {
    label: '',
    name: 'action',
    component: RHFReactNumberFormat,
    minWidth: 50,
    width: 50,
    show: true,
  },
];

export const BUSINESS_ASSETS_TABLE_STRUCTURE = (ffConfig) => [
  {
    label: 'Company Name',
    name: 'companyName',
    component: RHFTextField,
    minWidth: 150,
    width: 150,
    show: ffConfig?.businessAssets?.fields?.companyName?.active ?? true,
  },
  {
    label: 'Location',
    name: 'location',
    component: RHFTextField,
    minWidth: 150,
    width: 150,
    show: ffConfig?.businessAssets?.fields?.location?.active ?? true,
  },
  {
    label: 'Shareholder Percentage',
    name: 'shareholderPercentage',
    component: RHFReactNumberFormat,
    textAlign: 'right',
    decimalScale: 2,
    InputProps: {
      endAdornment: <InputAdornment position="end">%</InputAdornment>,
    },
    minWidth: 200,
    width: 200,
    show: ffConfig?.businessAssets?.fields?.shareholderPercentage?.active ?? true,
  },
  {
    label: 'Cap/Int',
    name: 'capOrInt',
    component: RHFAutocomplete,
    options: CAPintObject,
    minWidth: 150,
    width: 150,
    show: ffConfig?.businessAssets?.fields?.capOrInt?.active ?? true,
  },
  {
    label: 'Income Amount',
    name: 'incomeAmount',
    component: RHFReactNumberFormat,
    decimalScale: 2,
    thousandSeparator: true,
    fixedDecimalScale: true,
    prefix: true,
    textAlign: 'right',
    minWidth: 150,
    width: 150,
    show: ffConfig?.businessAssets?.fields?.incomeAmount?.active ?? true,
  },
  {
    label: 'Interest Rate',
    name: 'interestRate',
    component: RHFReactNumberFormat,
    textAlign: 'right',
    decimalScale: 2,
    InputProps: {
      endAdornment: <InputAdornment position="end">%</InputAdornment>,
    },
    minWidth: 150,
    width: 150,
    show: ffConfig?.businessAssets?.fields?.interestRate?.active ?? true,
  },
  {
    label: 'Value',
    name: 'value',
    component: RHFReactNumberFormat,
    decimalScale: 2,
    thousandSeparator: true,
    fixedDecimalScale: true,
    prefix: true,
    textAlign: 'right',
    minWidth: 150,
    width: 150,
    show: ffConfig?.businessAssets?.fields?.value?.active ?? true,
  },
  {
    label: 'Description',
    name: 'assetDescription',
    component: RHFTextField,
    minWidth: 150,
    width: 150,
    show: ffConfig?.businessAssets?.fields?.assetDescription?.active ?? true,
  },
  {
    label: '',
    name: 'action',
    component: RHFReactNumberFormat,
    minWidth: 50,
    width: 50,
    show: true,
  },
];

export const PENSION_ASSETS_TABLE_STRUCTURE = (ffConfig, pensionTypeOptions) => [
  {
    label: 'Pension Name',
    name: 'pensionName',
    component: RHFTextField,
    minWidth: 150,
    width: 150,
    show: ffConfig?.pensionAssets?.fields?.pensionName?.active ?? true,
  },
  {
    label: 'Pension Type',
    name: 'pensionType',
    component: RHFAutocomplete,
    options: pensionTypeOptions,
    minWidth: 150,
    width: 150,
    show: ffConfig?.pensionAssets?.fields?.pensionType?.active ?? true,
  },
  {
    label: 'Account Number',
    name: 'accountNumber',
    component: RHFTextField,
    minWidth: 150,
    width: 150,
    show: ffConfig?.pensionAssets?.fields?.accountNumber?.active ?? true,
  },
  {
    label: 'Provider',
    name: 'provider',
    component: RHFTextField,
    minWidth: 150,
    width: 150,
    show: ffConfig?.pensionAssets?.fields?.provider?.active ?? true,
  },
  {
    label: 'Value',
    name: 'cetv',
    component: RHFReactNumberFormat,
    decimalScale: 2,
    thousandSeparator: true,
    fixedDecimalScale: true,
    textAlign: 'right',
    prefix: true,
    minWidth: 150,
    width: 150,
    show: ffConfig?.pensionAssets?.fields?.cetv?.active ?? true,
  },
  {
    label: 'Transfer Value',
    name: 'transferValue',
    component: RHFReactNumberFormat,
    decimalScale: 2,
    thousandSeparator: true,
    fixedDecimalScale: true,
    textAlign: 'right',
    prefix: true,
    minWidth: 150,
    width: 150,
    show: ffConfig?.pensionAssets?.fields?.transferValue?.active ?? true,
  },
  {
    label: 'Start Date',
    name: 'startDate',
    component: RHFDatePicker,
    minWidth: 150,
    width: 150,
    show: ffConfig?.pensionAssets?.fields?.startDate?.active ?? true,
  },
  {
    label: 'End Date',
    name: 'endDate',
    component: RHFDatePicker,
    minWidth: 150,
    width: 150,
    show: ffConfig?.pensionAssets?.fields?.endDate?.active ?? true,
  },
  {
    label: 'Asset Location',
    name: 'assetLocation',
    component: RHFTextField,
    minWidth: 150,
    width: 150,
    show: ffConfig?.pensionAssets?.fields?.assetLocation?.active ?? true,
  },
  {
    label: 'Scheme',
    name: 'scheme',
    component: RHFTextField,
    minWidth: 150,
    width: 150,
    show: ffConfig?.pensionAssets?.fields?.scheme?.active ?? true,
  },
  // {
  //   label: 'Policy Number',
  //   name: 'policyNumber',
  //   component: RHFTextField,
  //   minWidth: 150,
  //   width: 150,
  //   show: ffConfig?.pensionAssets?.fields?.policyNumber?.active ?? true,
  // },
  {
    label: 'Description',
    name: 'assetDescription',
    component: RHFTextField,
    minWidth: 150,
    width: 150,
    show: ffConfig?.pensionAssets?.fields?.assetDescription?.active ?? true,
  },
  {
    label: '',
    name: 'action',
    component: RHFReactNumberFormat,
    minWidth: 50,
    width: 50,
    show: true,
  },
];

export const PROPERTY_ASSETS_TABLE_STRUCTURE = (ffConfig, propertyTypeOptions) => [
  {
    label: 'Property Name',
    name: 'propertyName',
    component: RHFTextField,
    minWidth: 150,
    width: 150,
    show: ffConfig?.propertyAssets?.fields?.propertyName?.active ?? true,
  },
  {
    label: 'Property Type',
    name: 'propertyType',
    component: RHFAutocomplete,
    options: propertyTypeOptions,
    minWidth: 150,
    width: 150,
    show: ffConfig?.propertyAssets?.fields?.propertyType?.active ?? true,
  },
  {
    label: 'Location',
    name: 'location',
    component: RHFTextField,
    minWidth: 150,
    width: 150,
    show: ffConfig?.propertyAssets?.fields?.location?.active ?? true,
  },
  {
    label: 'Income',
    name: 'income',
    component: RHFReactNumberFormat,
    decimalScale: 2,
    thousandSeparator: true,
    fixedDecimalScale: true,
    textAlign: 'right',
    prefix: true,
    minWidth: 150,
    width: 150,
    show: ffConfig?.propertyAssets?.fields?.income?.active ?? true,
  },
  {
    label: 'Cap/Int',
    name: 'capOrInt',
    component: RHFAutocomplete,
    options: CAPintObject,
    minWidth: 150,
    width: 150,
    show: ffConfig?.propertyAssets?.fields?.capOrInt?.active ?? true,
  },
  {
    label: 'Interest Rate',
    name: 'interestRate',
    component: RHFReactNumberFormat,
    textAlign: 'right',
    decimalScale: 2,
    InputProps: {
      endAdornment: <InputAdornment position="end">%</InputAdornment>,
    },
    minWidth: 150,
    width: 150,
    show: ffConfig?.propertyAssets?.fields?.interestRate?.active ?? true,
  },
  {
    label: 'Start Date',
    name: 'startDate',
    component: RHFDatePicker,
    minWidth: 150,
    width: 150,
    show: ffConfig?.propertyAssets?.fields?.startDate?.active ?? true,
  },
  {
    label: 'End Date',
    name: 'endDate',
    component: RHFDatePicker,
    minWidth: 150,
    width: 150,
    show: ffConfig?.propertyAssets?.fields?.endDate?.active ?? true,
  },
  {
    label: 'Current Value',
    name: 'currentValue',
    component: RHFReactNumberFormat,
    decimalScale: 2,
    thousandSeparator: true,
    fixedDecimalScale: true,
    textAlign: 'right',
    prefix: true,
    minWidth: 150,
    width: 150,
    show: ffConfig?.propertyAssets?.fields?.currentValue?.active ?? true,
  },
  {
    label: 'Description',
    name: 'assetDescription',
    component: RHFTextField,
    minWidth: 150,
    width: 150,
    show: ffConfig?.propertyAssets?.fields?.assetDescription?.active ?? true,
  },
  {
    label: '',
    name: 'action',
    component: RHFReactNumberFormat,
    minWidth: 50,
    width: 50,
    show: true,
  },
];

export const OTHER_ASSETS_TABLE_STRUCTURE = (ffConfig) => [
  {
    label: 'Asset Type',
    name: 'assetType',
    component: RHFTextField,
    minWidth: 150,
    width: 150,
    show: ffConfig?.otherAssets?.fields?.assetType?.active ?? true,
  },
  {
    label: 'Location',
    name: 'location',
    component: RHFTextField,
    minWidth: 150,
    width: 150,
    show: ffConfig?.otherAssets?.fields?.location?.active ?? true,
  },
  {
    label: 'Income',
    name: 'income',
    component: RHFReactNumberFormat,
    decimalScale: 2,
    thousandSeparator: true,
    fixedDecimalScale: true,
    textAlign: 'right',
    prefix: true,
    minWidth: 150,
    width: 150,
    show: ffConfig?.otherAssets?.fields?.income?.active ?? true,
  },
  {
    label: 'Cap/Int',
    name: 'capOrInt',
    component: RHFAutocomplete,
    options: CAPintObject,
    minWidth: 150,
    width: 150,
    show: ffConfig?.otherAssets?.fields?.capOrInt?.active ?? true,
  },
  {
    label: 'Interest Rate',
    name: 'interestRate',
    component: RHFReactNumberFormat,
    textAlign: 'right',
    decimalScale: 2,
    InputProps: {
      endAdornment: <InputAdornment position="end">%</InputAdornment>,
    },
    minWidth: 150,
    width: 150,
    show: ffConfig?.otherAssets?.fields?.interestRate?.active ?? true,
  },
  {
    label: 'Start Date',
    name: 'startDate',
    component: RHFDatePicker,
    minWidth: 150,
    width: 150,
    show: ffConfig?.otherAssets?.fields?.startDate?.active ?? true,
  },
  {
    label: 'End Date',
    name: 'endDate',
    component: RHFDatePicker,
    minWidth: 150,
    width: 150,
    show: ffConfig?.otherAssets?.fields?.endDate?.active ?? true,
  },
  {
    label: 'Current Value',
    name: 'currentValue',
    component: RHFReactNumberFormat,
    decimalScale: 2,
    thousandSeparator: true,
    fixedDecimalScale: true,
    textAlign: 'right',
    prefix: true,
    minWidth: 150,
    width: 150,
    show: ffConfig?.otherAssets?.fields?.currentValue?.active ?? true,
  },
  {
    label: 'Description',
    name: 'assetDescription',
    component: RHFTextField,
    minWidth: 150,
    width: 150,
    show: ffConfig?.otherAssets?.fields?.assetDescription?.active ?? true,
  },
  {
    label: '',
    name: 'action',
    component: RHFReactNumberFormat,
    minWidth: 50,
    width: 50,
    show: true,
  },
];
